/* KalaaDhara Bloom — storefront behaviour. No dependencies. */
(() => {
  'use strict';

  const B = window.Bloom || {};
  const $ = (sel, root = document) => root.querySelector(sel);
  const $$ = (sel, root = document) => Array.from(root.querySelectorAll(sel));

  /* ---------- Money ---------- */
  const money = (cents) => {
    cents = Math.round(Number(cents) || 0);
    if (B.currency === 'INR') {
      return (
        '₹' +
        (cents / 100).toLocaleString('en-IN', {
          minimumFractionDigits: cents % 100 ? 2 : 0,
          maximumFractionDigits: 2,
        })
      );
    }
    const amount = (cents / 100).toFixed(2);
    const noDecimals = String(Math.round(cents / 100));
    return (B.moneyFormat || '{{amount}}')
      .replace(/\{\{\s*amount_no_decimals[a-z_]*\s*\}\}/, noDecimals)
      .replace(/\{\{\s*amount[a-z_]*\s*\}\}/, amount);
  };

  /* ---------- Drawers ---------- */
  let lastOpener = null;
  const drawer = (name) => $(`[data-drawer="${name}"]`);

  const openDrawer = (name, opener) => {
    const el = drawer(name);
    if (!el) return;
    $$('.drawer.is-open').forEach((d) => d !== el && d.classList.remove('is-open'));
    lastOpener = opener || document.activeElement;
    el.classList.add('is-open');
    el.setAttribute('aria-hidden', 'false');
    document.body.classList.add('is-locked');
    const focusTarget = $('[data-drawer-focus]', el) || $('.drawer__panel button, .drawer__panel a', el);
    if (focusTarget) setTimeout(() => focusTarget.focus({ preventScroll: true }), 60);
  };

  const closeDrawers = () => {
    const open = $$('.drawer.is-open');
    if (!open.length) return;
    open.forEach((d) => {
      d.classList.remove('is-open');
      d.setAttribute('aria-hidden', 'true');
    });
    document.body.classList.remove('is-locked');
    if (lastOpener && document.contains(lastOpener)) lastOpener.focus({ preventScroll: true });
  };

  document.addEventListener('click', (e) => {
    const opener = e.target.closest('[data-drawer-open]');
    if (opener) {
      e.preventDefault();
      openDrawer(opener.dataset.drawerOpen, opener);
      return;
    }
    if (e.target.closest('[data-drawer-close]')) {
      e.preventDefault();
      closeDrawers();
    }
  });

  document.addEventListener('keydown', (e) => {
    if (e.key === 'Escape') {
      closeDrawers();
      const panel = $('[data-search-panel]');
      if (panel && !panel.hidden) toggleSearch(false);
    }
    if (e.key !== 'Tab') return;
    const open = $('.drawer.is-open .drawer__panel');
    if (!open) return;
    const items = $$('a[href], button:not([disabled]), input:not([type="hidden"]), select, textarea', open).filter(
      (n) => n.offsetParent !== null
    );
    if (!items.length) return;
    const first = items[0];
    const last = items[items.length - 1];
    if (e.shiftKey && document.activeElement === first) {
      e.preventDefault();
      last.focus();
    } else if (!e.shiftKey && document.activeElement === last) {
      e.preventDefault();
      first.focus();
    }
  });

  /* ---------- Search ---------- */
  const toggleSearch = (show) => {
    const panel = $('[data-search-panel]');
    if (!panel) return;
    const next = typeof show === 'boolean' ? show : panel.hidden;
    panel.hidden = !next;
    $$('[data-search-toggle]').forEach((b) => b.setAttribute('aria-expanded', String(next)));
    if (next) $('input[type="search"]', panel)?.focus();
  };

  document.addEventListener('click', (e) => {
    if (e.target.closest('[data-search-toggle]')) {
      e.preventDefault();
      toggleSearch();
    }
  });

  let searchTimer;
  let searchAbort;
  document.addEventListener('input', (e) => {
    const input = e.target.closest('[data-predictive-input]');
    if (!input) return;
    const out = $('[data-predictive]');
    if (!out) return;
    clearTimeout(searchTimer);
    const q = input.value.trim();
    if (q.length < 2) {
      out.innerHTML = '';
      return;
    }
    searchTimer = setTimeout(async () => {
      try {
        if (searchAbort) searchAbort.abort();
        searchAbort = new AbortController();
        const url = `${B.routes.predictive}?q=${encodeURIComponent(q)}&resources[type]=product&resources[limit]=6&section_id=predictive-search`;
        const res = await fetch(url, { signal: searchAbort.signal });
        if (!res.ok) return;
        const doc = new DOMParser().parseFromString(await res.text(), 'text/html');
        out.innerHTML = $('[data-predictive-results]', doc)?.innerHTML || '';
      } catch (err) {
        /* aborted or offline: keep whatever is shown */
      }
    }, 220);
  });

  /* ---------- Cart ---------- */
  const Cart = {
    sectionIds() {
      const ids = ['cart-drawer'];
      const page = $('[data-cart-page]');
      if (page) ids.push(page.dataset.sectionId);
      return ids;
    },

    render(sections) {
      if (!sections) return;
      const parse = (html) => new DOMParser().parseFromString(html, 'text/html');

      if (sections['cart-drawer']) {
        const fresh = $('#CartDrawerInner', parse(sections['cart-drawer']));
        const current = $('#CartDrawerInner');
        if (fresh && current) {
          current.innerHTML = fresh.innerHTML;
          current.dataset.count = fresh.dataset.count;
          this.setCount(fresh.dataset.count);
        }
      }
      const page = $('[data-cart-page]');
      if (page && sections[page.dataset.sectionId]) {
        const fresh = $('[data-cart-page]', parse(sections[page.dataset.sectionId]));
        if (fresh) page.innerHTML = fresh.innerHTML;
      }
      this.loadUpsell();
      document.dispatchEvent(new CustomEvent('bloom:cart'));
    },

    setCount(count) {
      $$('[data-cart-count]').forEach((n) => {
        n.textContent = count;
        n.dataset.count = count;
      });
    },

    async add(formData) {
      formData.append('sections', this.sectionIds().join(','));
      formData.append('sections_url', window.location.pathname);
      const res = await fetch(`${B.routes.cartAdd}.js`, {
        method: 'POST',
        headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
        body: formData,
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.description || data.message || 'Sorry, that could not be added.');
      this.render(data.sections);
      return data;
    },

    async change(key, quantity) {
      const res = await fetch(`${B.routes.cartChange}.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({
          id: key,
          quantity,
          sections: this.sectionIds(),
          sections_url: window.location.pathname,
        }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.description || data.message || 'Sorry, the cart could not be updated.');
      this.render(data.sections);
      return data;
    },

    upsellCache: {},
    async loadUpsell() {
      if (!B.upsell) return;
      const slot = $('#CartDrawerInner [data-upsell]');
      if (!slot) return;
      const id = slot.dataset.productId;
      if (!id) return;
      try {
        if (!this.upsellCache[id]) {
          const url = `${B.routes.recommendations}?product_id=${id}&limit=6&intent=related&section_id=cart-upsell`;
          const res = await fetch(url);
          if (!res.ok) return;
          this.upsellCache[id] = await res.text();
        }
        const doc = new DOMParser().parseFromString(this.upsellCache[id], 'text/html');
        const inCart = (slot.dataset.inCart || '').split(',');
        $$('[data-upsell-item]', doc).forEach((item) => {
          if (inCart.includes(item.dataset.productId)) item.remove();
        });
        const body = $('[data-upsell-body]', doc);
        const current = $('#CartDrawerInner [data-upsell]');
        if (current && body && $('[data-upsell-item]', body)) current.innerHTML = body.innerHTML;
      } catch (err) {
        /* suggestions are optional */
      }
    },
  };

  // Open cart drawer from header (except on the cart page itself)
  document.addEventListener('click', (e) => {
    const btn = e.target.closest('[data-cart-open]');
    if (!btn || document.body.classList.contains('t-cart')) return;
    e.preventDefault();
    openDrawer('cart', btn);
    Cart.loadUpsell();
  });

  // Quick add + product form
  document.addEventListener('submit', async (e) => {
    const form = e.target.closest('[data-ajax-add], [data-product-form]');
    if (!form) return;
    // "Buy it now" (dynamic checkout) submits natively.
    if (e.submitter && e.submitter.closest('.shopify-payment-button')) return;
    e.preventDefault();

    const btn = e.submitter || $('[type="submit"]', form);
    const error = $('[data-form-error]', form) || $(`[data-form-error="${form.id}"]`);
    if (error) error.hidden = true;
    btn?.classList.add('is-loading');
    try {
      // The motion layer (if loaded) flies the product to the cart while the request runs.
      await Promise.all([Cart.add(new FormData(form)), window.Bloom.fly ? window.Bloom.fly(form) : null]);
      if (document.body.classList.contains('t-cart')) {
        window.scrollTo({ top: 0, behavior: 'smooth' });
      } else {
        openDrawer('cart', btn);
      }
      if (btn?.classList.contains('card__add')) {
        btn.classList.add('is-done');
        setTimeout(() => btn.classList.remove('is-done'), 1600);
      }
    } catch (err) {
      if (error) {
        error.textContent = err.message;
        error.hidden = false;
      } else {
        window.alert(err.message);
      }
    } finally {
      btn?.classList.remove('is-loading');
    }
  });

  // Cart line quantity / remove
  const lineUpdate = async (el, quantity) => {
    const line = el.closest('[data-line]');
    if (!line) return;
    line.style.opacity = '0.5';
    try {
      await Cart.change(line.dataset.line, Math.max(0, quantity));
    } catch (err) {
      line.style.opacity = '';
      window.alert(err.message);
    }
  };

  document.addEventListener('click', (e) => {
    const step = e.target.closest('[data-line-step]');
    if (step) {
      e.preventDefault();
      const input = $('input', step.closest('.qty'));
      lineUpdate(step, (parseInt(input.value, 10) || 0) + Number(step.dataset.lineStep));
      return;
    }
    const remove = e.target.closest('[data-line-remove]');
    if (remove) {
      e.preventDefault();
      lineUpdate(remove, 0);
    }
  });

  document.addEventListener('change', (e) => {
    const input = e.target.closest('[data-line-qty]');
    if (input) lineUpdate(input, parseInt(input.value, 10) || 0);
  });

  /* ---------- Generic quantity stepper (product page) ---------- */
  document.addEventListener('click', (e) => {
    const step = e.target.closest('[data-qty-step]');
    if (!step) return;
    const input = $('input', step.closest('.qty'));
    const min = Number(input.min) || 1;
    input.value = Math.max(min, (parseInt(input.value, 10) || min) + Number(step.dataset.qtyStep));
    input.dispatchEvent(new Event('change', { bubbles: true }));
  });

  /* ---------- Rails (carousels) ---------- */
  document.addEventListener('click', (e) => {
    const btn = e.target.closest('[data-rail-prev], [data-rail-next]');
    if (!btn) return;
    const scope = btn.closest('[data-rail-scope]') || document;
    const rail = $('.rail:not([hidden])', scope);
    if (!rail) return;
    const dir = btn.hasAttribute('data-rail-next') ? 1 : -1;
    rail.scrollBy({ left: dir * rail.clientWidth * 0.8, behavior: 'smooth' });
  });

  /* ---------- Tabs ---------- */
  document.addEventListener('click', (e) => {
    const tab = e.target.closest('[data-tab]');
    if (!tab) return;
    const scope = tab.closest('[data-tabs]');
    if (!scope) return;
    $$('[data-tab]', scope).forEach((t) => t.setAttribute('aria-selected', String(t === tab)));
    $$('[data-tab-panel]', scope).forEach((p) => {
      p.hidden = p.dataset.tabPanel !== tab.dataset.tab;
    });
  });

  /* ---------- Collection sort ---------- */
  document.addEventListener('change', (e) => {
    const sort = e.target.closest('[data-sort]');
    if (!sort) return;
    const url = new URL(window.location.href);
    url.searchParams.set('sort_by', sort.value);
    url.searchParams.delete('page');
    window.location.href = url.toString();
  });

  /* ---------- Scroll reveal ---------- */
  const reveal = (root = document) => {
    const nodes = $$('[data-reveal]:not(.is-in)', root);
    if (!('IntersectionObserver' in window)) {
      nodes.forEach((n) => n.classList.add('is-in'));
      return;
    }
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          entry.target.classList.add('is-in');
          io.unobserve(entry.target);
        });
      },
      { rootMargin: '0px 0px -8% 0px', threshold: 0.08 }
    );
    nodes.forEach((n) => io.observe(n));
  };

  /* ---------- Product recommendations ---------- */
  const loadRecommendations = (root = document) => {
    $$('[data-recommendations]', root).forEach(async (el) => {
      if (el.dataset.loaded) return;
      el.dataset.loaded = '1';
      try {
        const res = await fetch(el.dataset.url);
        if (!res.ok) return;
        const doc = new DOMParser().parseFromString(await res.text(), 'text/html');
        const fresh = $('[data-recommendations]', doc);
        if (fresh && fresh.innerHTML.trim()) {
          el.innerHTML = fresh.innerHTML;
          el.hidden = false;
          reveal(el);
          document.dispatchEvent(new CustomEvent('bloom:content', { detail: el }));
        }
      } catch (err) {
        /* optional content */
      }
    });
  };

  /* ---------- Product page ---------- */
  const initProduct = (root) => {
    if (root.dataset.ready) return;
    root.dataset.ready = '1';

    const variants = JSON.parse($('[data-variants]', root)?.textContent || '[]');
    const form = $('[data-product-form]', root);
    const idInput = form ? $('input[name="id"]', form) : null;
    const qtyInput = $('[data-pdp-qty]', root);
    const atc = $('[data-atc]', root);
    const atcLabel = $('[data-atc-label]', root);
    const priceEl = $('[data-pdp-price]', root);
    const sticky = $('[data-sticky-atc]');
    const track = $('.gallery__track', root);
    const dots = $$('.gallery__dots i', root);
    const thumbs = $$('.gallery__thumb', root);
    const slides = $$('.gallery__slide', root);

    let current = variants.find((v) => String(v.id) === String(idInput?.value)) || variants[0];

    /* Gallery */
    const thumbStrip = $('.gallery__thumbs', root);
    const setActive = (index) => {
      dots.forEach((d, i) => d.classList.toggle('is-active', i === index));
      thumbs.forEach((t, i) => t.classList.toggle('is-active', i === index));
      const thumb = thumbs[index];
      if (thumb && thumbStrip && thumbStrip.scrollWidth > thumbStrip.clientWidth) {
        const offset = thumb.getBoundingClientRect().left - thumbStrip.getBoundingClientRect().left;
        thumbStrip.scrollTo({ left: thumbStrip.scrollLeft + offset - (thumbStrip.clientWidth - thumb.offsetWidth) / 2, behavior: 'smooth' });
      }
    };
    const goTo = (index, smooth = true) => {
      if (!track || !slides[index]) return;
      track.scrollTo({ left: slides[index].offsetLeft, behavior: smooth ? 'smooth' : 'auto' });
      setActive(index);
    };
    if (track) {
      let raf;
      track.addEventListener(
        'scroll',
        () => {
          cancelAnimationFrame(raf);
          raf = requestAnimationFrame(() => setActive(Math.round(track.scrollLeft / track.clientWidth)));
        },
        { passive: true }
      );
    }
    thumbs.forEach((thumb, i) => thumb.addEventListener('click', () => goTo(i)));
    /* A link to one design (?variant=…) opens on that design's photo. */
    if (current && current.media && new URL(window.location.href).searchParams.has('variant')) {
      const start = slides.findIndex((s) => s.dataset.mediaId === String(current.media));
      if (start > 0) goTo(start, false);
    }

    /* Pricing */
    const bundleInputs = $$('[data-bundle]', root);
    const quantity = () => Math.max(1, parseInt(qtyInput?.value, 10) || 1);
    const bundlePct = () => {
      const checked = bundleInputs.find((b) => b.checked);
      return checked ? Number(checked.dataset.pct) || 0 : 0;
    };
    const lineTotal = (qty, pct) => Math.round((current.price * qty * (100 - pct)) / 100);

    const renderPrice = () => {
      if (!current) return;
      if (priceEl) {
        const onSale = current.compare > current.price;
        const saving = onSale ? Math.round(((current.compare - current.price) * 100) / current.compare) : 0;
        priceEl.innerHTML =
          `<span class="price${onSale ? ' price--sale' : ''}">` +
          `<span class="price__now">${money(current.price)}</span>` +
          (onSale
            ? `<s class="price__was"><span class="visually-hidden">Was </span>${money(current.compare)}</s>` +
              (saving > 0 ? `<span class="pill pill--save">Save ${saving}%</span>` : '')
            : '') +
          '</span>';
      }
      bundleInputs.forEach((input) => {
        const qty = Number(input.value);
        const pct = Number(input.dataset.pct) || 0;
        const card = input.closest('.bundle');
        const now = $('[data-bundle-now]', card);
        const was = $('[data-bundle-was]', card);
        if (now) now.textContent = money(lineTotal(qty, pct));
        if (was) {
          const full = (current.compare > current.price ? current.compare : current.price) * qty;
          was.textContent = full > lineTotal(qty, pct) ? money(full) : '';
        }
      });
      const total = money(lineTotal(quantity(), bundlePct()));
      if (atcLabel) {
        atcLabel.textContent = current.available ? `${atc.dataset.label} · ${total}` : atc.dataset.soldOut;
      }
      if (atc) atc.disabled = !current.available;
      $$('[data-buy-now]', root).forEach((n) => (n.hidden = !current.available));
      if (sticky) {
        const stickyPrice = $('[data-sticky-price]', sticky);
        const stickyBtn = $('[data-sticky-btn]', sticky);
        if (stickyPrice) stickyPrice.textContent = total;
        if (stickyBtn) stickyBtn.disabled = !current.available;
      }
      $$('[data-dispatch]', root).forEach((n) => {
        n.textContent = current.stock ? n.dataset.inStock : n.dataset.madeToOrder;
      });
    };

    /* Variant selection */
    const optionInputs = $$('[data-opt]', root);
    const selectedOptions = () => {
      const picked = [];
      optionInputs.forEach((input) => {
        if (input.checked) picked[Number(input.dataset.opt)] = input.value;
      });
      return picked;
    };
    const onOptionChange = () => {
      const picked = selectedOptions();
      const match = variants.find((v) => v.options.every((value, i) => value === picked[i]));
      $$('[data-opt-current]', root).forEach((n) => {
        n.textContent = picked[Number(n.dataset.optCurrent)] || '';
      });
      if (!match) {
        if (atc) atc.disabled = true;
        if (atcLabel) atcLabel.textContent = 'Unavailable';
        return;
      }
      current = match;
      if (idInput) idInput.value = match.id;
      const url = new URL(window.location.href);
      url.searchParams.set('variant', match.id);
      window.history.replaceState({}, '', url.toString());
      if (match.media) {
        const index = slides.findIndex((s) => s.dataset.mediaId === String(match.media));
        if (index > -1) goTo(index);
      }
      renderPrice();
    };
    optionInputs.forEach((input) => input.addEventListener('change', onOptionChange));

    bundleInputs.forEach((input) =>
      input.addEventListener('change', () => {
        if (qtyInput) qtyInput.value = input.value;
        renderPrice();
      })
    );
    qtyInput?.addEventListener('change', renderPrice);

    renderPrice();

    /* Sticky add to cart */
    if (sticky && atc && 'IntersectionObserver' in window) {
      const io = new IntersectionObserver(
        ([entry]) => {
          const show = !entry.isIntersecting;
          sticky.classList.toggle('is-visible', show);
          document.body.classList.toggle('has-sticky-atc', show);
        },
        { threshold: 0 }
      );
      io.observe(atc);
      $('[data-sticky-btn]', sticky)?.addEventListener('click', () => form?.requestSubmit(atc));
    }
  };

  /* ---------- Long descriptions: "Read more" ---------- */
  const initClamps = (root = document) => {
    $$('[data-clamp]', root).forEach((box) => {
      const btn = box.parentElement.querySelector('[data-clamp-toggle]');
      if (box.scrollHeight <= box.clientHeight + 40) {
        box.classList.add('is-open');
        return;
      }
      if (!btn || btn.dataset.ready) return;
      btn.dataset.ready = '1';
      btn.hidden = false;
      btn.addEventListener('click', () => {
        const open = box.classList.toggle('is-open');
        btn.textContent = open ? 'Show less' : 'Read more';
        if (!open) box.closest('details')?.scrollIntoView({ block: 'nearest' });
      });
    });
  };

  /* ---------- Hide the reviews block if the reviews app renders nothing ---------- */
  const initReviews = () => {
    const section = $('[data-reviews]');
    if (!section) return;
    const check = () => {
      const wrap = $('.reviews-wrap', section);
      section.hidden = !wrap || wrap.offsetHeight < 60;
    };
    setTimeout(check, 4000);
    setTimeout(check, 9000);
  };

  /* ---------- Floating WhatsApp button appears once the shopper starts scrolling ---------- */
  const onScrollState = () => document.documentElement.classList.toggle('is-scrolled', window.scrollY > 220);
  window.addEventListener('scroll', onScrollState, { passive: true });
  onScrollState();

  /* ---------- Boot ---------- */
  const boot = (root = document) => {
    reveal(root);
    initClamps(root);
    loadRecommendations(root);
    $$('[data-pdp]', root).forEach(initProduct);
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => {
      boot();
      initReviews();
    });
  } else {
    boot();
    initReviews();
  }

  // Theme editor: re-run when a section is added or re-rendered.
  document.addEventListener('shopify:section:load', (e) => boot(e.target));
  document.addEventListener('shopify:section:select', (e) => {
    if (e.target.querySelector('[data-drawer="cart"]')) openDrawer('cart');
  });
  document.addEventListener('shopify:section:deselect', (e) => {
    if (e.target.querySelector('[data-drawer="cart"]')) closeDrawers();
  });

  window.Bloom = Object.assign(B, { money, openDrawer, closeDrawers, Cart });
})();
